import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ServiceHero from "../../components/ServiceHero";
import SidebarForm from "../../components/SidebarForm";
import { EprCertificateContainer } from "./EprCertificate.styles.js";

const GemRegistration = () => {
  return (
    <>
      <Header />
      <ServiceHero
        title="GEM Registration"
        subtitle="End-to-end support for registering sellers & service providers on Government e-Marketplace (GeM)."
        breadcrumb="GEM Registration"
      />
      <EprCertificateContainer>
        <section className="epr-section" aria-labelledby="epr-heading">
          <div className="epr-container">
            <article className="epr-main">
              <h1 id="epr-heading">GEM (Government e-Marketplace) Registration & Compliance</h1>

              <div className="byline">
                Professional support for GeM seller onboarding, product listing,
                catalog approval, bid participation and overall compliance.
              </div>

              <div
                className="section-lottie"
                data-lottie="business-analytics"
                data-lottie-width="420px"
                data-lottie-height="320px"
              ></div>

              <p>
                Government e-Marketplace (GeM) is a centralized procurement platform
                used by all Government departments, PSUs and ministries. Any business
                — manufacturer, trader, service provider or startup — can register to
                sell products and services to Government buyers. We assist with complete
                onboarding, catalog creation, compliance documentation and tender/bid
                guidance.
              </p>

              <h2>Scope & Applicability</h2>
              <p>
                GeM registration is applicable to all entities supplying goods or
                services such as manufacturers, traders, service providers, MSMEs,
                startups and OEMs. It is mandatory for selling to Government buyers
                through the portal.
              </p>

              <h2>Key GeM Registration & Compliance Areas</h2>
              <div className="feature-grid">
                <div className="feature-card small-animate">
                  <i className="fas fa-recycle" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Seller Registration</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Assistance with creating a GeM business account, verification and
                    primary documentation submission.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-bolt" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Product & Service Listing</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Catalog setup, category selection, specification mapping, pricing
                    strategy and listing approval.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-microchip" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>OEM Panel Approval</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Support for OEM brand approval, brand authorization and compliance
                    documentation.
                  </p>
                </div>

                <div className="feature-card small-animate">
                  <i className="fas fa-oil-can" style={{ color: "var(--primary-gold)" }}></i>
                  <h4>Bid Participation Support</h4>
                  <p style={{ color: "var(--text-secondary)", margin: "6px 0 0" }}>
                    Tender monitoring, bid analysis, technical support and bid
                    submission assistance.
                  </p>
                </div>
              </div>

              <h2>GeM Registration Process</h2>
              <ol>
                <li>Create GeM seller account using Aadhaar/PAN & business details.</li>
                <li>Upload registration documents and complete verification.</li>
                <li>Add products or services with full specifications & pricing.</li>
                <li>Apply for brand approval/OEM panel (if required).</li>
                <li>Begin participating in bids & Government tenders.</li>
              </ol>

              <h2>Documentation Required</h2>
              <ul>
                <li>Business registration certificate (GST/INC/MSME/Udyam)</li>
                <li>PAN card & Aadhaar of the authorized person</li>
                <li>Bank account details</li>
                <li>Product/service catalogue & specifications</li>
                <li>Brand authorization letter (if applicable)</li>
              </ul>

              <div style={{ textAlign: "center", margin: "22px 0" }}>
                <svg width="100%" height="36" viewBox="0 0 1200 36" preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="g-epr" x1="0" x2="1">
                      <stop offset="0%" stopColor="#1C2951" stopOpacity="0.12" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.06" />
                    </linearGradient>
                  </defs>
                  <rect width="1200" height="36" fill="url(#g-epr)" rx="6" />
                </svg>
              </div>

              <h2>Common Compliance Requirements</h2>
              <p>Every seller must maintain accurate listings, pricing and documentation:</p>
              <div className="highlight-box">
                <p>
                  <strong>Example:</strong> Incorrect product specifications, misleading
                  images or improper pricing can lead to suspension of the GeM account
                  or product de-listing.
                </p>
              </div>

              <h2>Inspection & Verification</h2>
              <p>
                GeM authorities may verify product authenticity, brand ownership,
                OEM status, pricing justification and service capabilities. We assist
                during audits, clarifications and compliance checks.
              </p>

              <h2>Penalties & Non-Compliance</h2>
              <p>
                Violations such as misleading listings, non-delivery, fake documents or
                irregular pricing may lead to penalties, blacklisting or account
                suspension. We help with representation and compliance corrections.
              </p>

              

              <h2>Case Studies</h2>
              <p>
                <strong>Electronics Trader:</strong> Successfully registered and secured
                OEM approval, enabling participation in high-value PSU tenders.
                <br />
                <strong>IT Services Firm:</strong> Listed services, optimized catalog and
                won first GeM service contract within 45 days.
              </p>

              <h2>FAQs</h2>
              <div className="highlight-box">
                <h3>Is GeM registration mandatory?</h3>
                <p>No, but without GeM registration you cannot sell to Government buyers.</p>
              </div>

              <div className="highlight-box">
                <h3>Can a trader list products without OEM approval?</h3>
                <p>Yes, but certain product categories require brand approval or OEM authorization.</p>
              </div>

              <div style={{ textAlign: "center", marginTop: "18px" }}>
                <a
                  href="#epr-contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("epr-contact").scrollIntoView({ behavior: "smooth" });
                  }}
                  className="btn-primary"
                  style={{ maxWidth: "300px", display: "inline-block" }}
                >
                  Request GeM Registration Help
                </a>
              </div>
            </article>

            <aside className="epr-sidebar">
              <SidebarForm />
            </aside>
          </div>
        </section>
      </EprCertificateContainer>
      <Footer />
    </>
  );
};

export default GemRegistration;
